import { IconButton, OnOffSwitch } from "@similarweb/ui-components";

import { requestFeature } from "pro-features/components/Modals/src/HookModal/services/requestFeature";
import { translate } from "app/filters/i18nFilter";
import { CrmManagementDisabledTooltip } from "app/pages/sales-intelligence/common-components/CrmManagementDisabledTooltip";
import { SI_CONTACTS_HOOK_ID } from "app/pages/sales-intelligence/constants/common";
import integrationTrackingService from "app/pages/sales-intelligence/services/tracking/integrationTrackingService";
import { commonKeys } from "app/pages/sales-intelligence/utils/i18n/common";
import { WrappedWithPreloader } from "app/pages/sales-intelligence/pages/management/components/common/WrappedWithPreloader/WrappedWithPreloader";

import {
    MANAGEMENT_EXPORT_PREFERENCES_REQUIRED_FIELDS_WARNING,
    MANAGEMENT_EXPORT_PREFERENCES_MAPPED_FIELDS,
} from "../../../../common-components/export-table/translations";
import { useExportToggleProps } from "../../hooks/useExportToggleProps";

import {
    StyledExportLabel,
    StyledExportSwitchContainer,
    StyledExportToggle,
    StyledLockedState,
    StyledLockIcon,
    StyledMandatoryFieldsWarning,
    StyledMappedFields,
} from "./style";
import type { ExportToggleProps } from "./type";

const ExportToggle = ({ integrationName, toggleName, onGainAccessClick }: ExportToggleProps) => {
    const { value, isLoading, isLocked, text, mappedFieldsCount, totalFields, missingRequiredFieldsCount, onClick } =
        useExportToggleProps(integrationName, toggleName);

    const handleGainAccessClick = () => {
        integrationTrackingService.trackRequestAccess(integrationName);
        requestFeature(SI_CONTACTS_HOOK_ID);
        onGainAccessClick();
    };

    const renderOnOffSwitch = (isReadOnly: boolean) => (
        <OnOffSwitch
            isSelected={value}
            onClick={onClick}
            isDisabled={isReadOnly}
        />
    );

    const renderSwitch = () => {
        return isLocked ? (
            <StyledLockedState>
                <StyledLockIcon
                    iconName="locked"
                    size="sm"
                />
                <StyledExportLabel>{translate(text)}</StyledExportLabel>
            </StyledLockedState>
        ) : (
            <StyledExportSwitchContainer>
                <CrmManagementDisabledTooltip renderContent={renderOnOffSwitch} />
                <StyledExportLabel>{translate(text)}</StyledExportLabel>
            </StyledExportSwitchContainer>
        );
    };

    const renderUpgrade = () => {
        if (isLocked) {
            return (
                <IconButton
                    iconName="sparkles"
                    type="trial"
                    onClick={handleGainAccessClick}
                    data-automation={`${integrationName}-export-toggle-${toggleName}-gain-access`}
                >
                    {translate(commonKeys.gainAccess)}
                </IconButton>
            );
        }

        const mappedFieldsCountText = `${mappedFieldsCount}/${totalFields} ${translate(
            MANAGEMENT_EXPORT_PREFERENCES_MAPPED_FIELDS,
        )} `;

        const shouldShowWarning = missingRequiredFieldsCount > 0 && value;

        const plurality = missingRequiredFieldsCount === 1 ? "singular" : "plural";

        const missingRequiredFieldsMessage = translate(
            MANAGEMENT_EXPORT_PREFERENCES_REQUIRED_FIELDS_WARNING[plurality],
            { count: missingRequiredFieldsCount },
        );

        return (
            <>
                {shouldShowWarning ? (
                    <StyledMandatoryFieldsWarning>{missingRequiredFieldsMessage}</StyledMandatoryFieldsWarning>
                ) : null}
                <StyledMappedFields>{mappedFieldsCountText}</StyledMappedFields>
            </>
        );
    };

    return (
        <StyledExportToggle data-automation={`${integrationName}-export-toggle-${toggleName}`}>
            <WrappedWithPreloader
                isLoading={isLoading}
                height={20}
                width="90%"
            >
                {renderSwitch()}
                {renderUpgrade()}
            </WrappedWithPreloader>
        </StyledExportToggle>
    );
};

export default ExportToggle;
